import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmCancellation: (reason: string, feedback: string) => void;
}

const ModalBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md};
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  width: 100%;
  max-width: 500px;
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  text-align: center;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: ${theme.spacing.xs};
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border: 1px solid var(--border-medium);
  color: var(--text-primary);
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--border-dark);
  }
  
  input:checked + & {
    border-color: ${theme.colors.primary};
    background: rgba(236, 100, 53, 0.05);
  }
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${theme.colors.primary};
`;

const FeedbackTextarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid var(--border-medium);
  border-radius: ${theme.borderRadius.md};
  background: var(--bg-secondary);
  color: var(--text-primary);
  min-height: 100px;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 1rem;
  
  ${props => {
    switch (props.$variant) {
      case 'danger':
        return `
          background: #ffebeb;
          color: #d93025;
          &:hover { background: #ffdcdc; }
        `;
      case 'secondary':
        return `
          background: var(--bg-secondary);
          color: var(--text-primary);
          &:hover { background: var(--bg-tertiary); }
        `;
      default: // primary (keep subscription)
        return `
          background: ${theme.colors.primary};
          color: white;
          &:hover { opacity: 0.9; }
        `;
    }
  }}
`;

const ErrorMessage = styled.div`
  color: #d93025;
  background: #ffebeb;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: ${theme.spacing.sm};
`;

const CANCELLATION_REASONS = [
  'Found a match!',
  'Too expensive',
  'App quality issues',
  'Taking a break',
  'Other'
];

const getFeedbackPlaceholder = (reason: string): string => {
  switch (reason) {
    case 'Found a match!':
      return "That's amazing! We'd love to hear a bit about your success story.";
    case 'Too expensive':
      return "What features did you find most valuable? What price point would make sense for you?";
    case 'App quality issues':
      return "Please describe the issues you encountered so we can fix them.";
    case 'Taking a break':
      return "We hope to see you back soon! Is there anything specific that led to this break?";
    case 'Other':
      return "Please tell us more about your reason for leaving.";
    default:
      return "Tell us more about your experience...";
  }
};

export const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onConfirmCancellation
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!selectedReason) {
      setError('Please select a reason for cancellation to proceed.');
      return;
    }
    onConfirmCancellation(selectedReason, feedback);
  };

  const handleReasonChange = (reason: string) => {
    setSelectedReason(reason);
    setFeedback(''); // Clear feedback when reason changes to match new prompt
    if (error) setError('');
  };

  return (
    <ModalBackdrop $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>We're sorry to see you go</Title>
          <Subtitle>
            Before you cancel, can you tell us what went wrong? Your feedback helps us improve.
          </Subtitle>
        </ModalHeader>

        <OptionsContainer>
          {CANCELLATION_REASONS.map(reason => (
            <RadioLabel key={reason}>
              <RadioInput
                type="radio"
                name="cancellation-reason"
                value={reason}
                checked={selectedReason === reason}
                onChange={(e) => handleReasonChange(e.target.value)}
              />
              {reason}
            </RadioLabel>
          ))}
        </OptionsContainer>

        {selectedReason && (
          <div>
            <Subtitle style={{ marginBottom: '8px', fontWeight: 600 }}>
              Anything else you'd like to share?
            </Subtitle>
            <FeedbackTextarea
              placeholder={getFeedbackPlaceholder(selectedReason)}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button $variant="secondary" onClick={onClose}>
            Keep Subscription
          </Button>
          <Button $variant="danger" onClick={handleSubmit}>
            Confirm Cancellation
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalBackdrop>
  );
};
